/**
 * Date, time and number formatting shared by The Engine and the field app (D34, D35).
 *
 * - Digits are always Western (0–9), in both languages.
 * - `text` style, for dates in UI text: day + month name + year, "17 سبتمبر 2026" / "17 September 2026".
 * - `iso` style, for tables, filters and exports: "2026-09-17".
 * - Times are 24-hour: "14:05".
 *
 * Month names come from fixed tables instead of Intl, so output is identical in
 * browsers, Node and Hermes (React Native) regardless of installed locale data.
 * Values are shown in the viewer's local time zone.
 */
export const MONTHS_AR = [
    'يناير',
    'فبراير',
    'مارس',
    'أبريل',
    'مايو',
    'يونيو',
    'يوليو',
    'أغسطس',
    'سبتمبر',
    'أكتوبر',
    'نوفمبر',
    'ديسمبر',
];
export const MONTHS_EN = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const EMPTY = '—';
const pad = (value) => String(value).padStart(2, '0');
/** Parses Date | epoch ms | ISO string. A bare `YYYY-MM-DD` is read as a local calendar day (no UTC shift). */
export function toDate(value) {
    if (value === null || value === undefined || value === '')
        return null;
    if (value instanceof Date)
        return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string') {
        const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (day)
            return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
/** `17 سبتمبر 2026` / `17 September 2026` (text) or `2026-09-17` (iso). */
export function formatDate(value, language, style = 'text') {
    const date = toDate(value);
    if (!date)
        return EMPTY;
    if (style === 'iso')
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const months = language === 'ar' ? MONTHS_AR : MONTHS_EN;
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}
/** 24-hour `14:05`. */
export function formatTime(value) {
    const date = toDate(value);
    return date ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : EMPTY;
}
/** `17 سبتمبر 2026، 14:05` / `17 September 2026, 14:05` (text) or `2026-09-17 14:05` (iso). */
export function formatDateTime(value, language, style = 'text') {
    const date = toDate(value);
    if (!date)
        return EMPTY;
    if (style === 'iso')
        return `${formatDate(date, language, 'iso')} ${formatTime(date)}`;
    return `${formatDate(date, language, 'text')}${language === 'ar' ? '،' : ','} ${formatTime(date)}`;
}
/** `09-17` for chart axes, from a date or a `YYYY-MM-DD` day key. */
export function formatDayShort(value) {
    const iso = formatDate(value, 'en', 'iso');
    return iso === EMPTY ? iso : iso.slice(5);
}
/** `1,204` (Western digits, comma grouping). */
export function formatNumber(value) {
    if (value === null || value === undefined || !Number.isFinite(value))
        return EMPTY;
    const [whole, fraction] = String(Math.abs(value)).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${value < 0 ? '-' : ''}${grouped}${fraction ? `.${fraction}` : ''}`;
}
/** `98.2%` from a 0–1 ratio, at most one decimal. */
export function formatPercent(value) {
    if (value === null || value === undefined || !Number.isFinite(value))
        return EMPTY;
    return `${formatNumber(Math.round(value * 1000) / 10)}%`;
}
//# sourceMappingURL=format.js.map